import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/utils/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TrendingUp, TrendingDown, DollarSign, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Finances() {
  const { t } = useTranslation();
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [countryInfo, setCountryInfo] = useState(null);

  const [incomeForm, setIncomeForm] = useState({
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    category: "service",
    vat_rate: 0,
    project_id: ""
  });

  const [expenseForm, setExpenseForm] = useState({
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    category: "office",
    vat_rate: 0,
    is_deductible: true
  });

  useEffect(() => {
    fetchIncome();
    fetchExpenses();
    fetchProjects();
    fetchCountryInfo();
  }, []);
  
  // Set default VAT rate when country info is loaded
  useEffect(() => {
    if (countryInfo && countryInfo.vat_rates && countryInfo.vat_rates.length > 0) {
      const defaultRate = countryInfo.vat_rates[0].rate;
      setIncomeForm(prev => ({ ...prev, vat_rate: prev.vat_rate === 0 ? defaultRate : prev.vat_rate }));
      setExpenseForm(prev => ({ ...prev, vat_rate: prev.vat_rate === 0 ? defaultRate : prev.vat_rate }));
    }
  }, [countryInfo]);

  const fetchCountryInfo = async () => {
    try {
      const response = await api.get('/country/info');
      setCountryInfo(response.data);
    } catch (error) {
      console.error("Error fetching country info:", error);
    }
  };

  const fetchIncome = async () => {
    try {
      const response = await api.get('/incomes');
      setIncome(response.data);
    } catch (error) {
      console.error("Error fetching income:", error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/expenses');
      setExpenses(response.data);
    } catch (error) {
      console.error("Error fetching expenses:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedIncome) {
        await api.put(`/finances/income/${selectedIncome.id}`, incomeForm);
      } else {
        await api.post('/finances/income', incomeForm);
      }
      toast.success(t('common.saved', 'Saved'));
      fetchIncome();
      setIncomeDialogOpen(false);
      resetIncomeForm();
    } catch (error) {
      console.error("Error saving income:", error);
      toast.error(t('finances.saveError', 'Error saving'));
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedExpense) {
        await api.put(`/expenses/${selectedExpense.id}`, expenseForm);
      } else {
        await api.post('/expenses', expenseForm);
      }
      toast.success(t('common.saved', 'Saved'));
      fetchExpenses();
      setExpenseDialogOpen(false);
      resetExpenseForm();
    } catch (error) {
      console.error("Error saving expense:", error);
      toast.error(t('finances.saveError', 'Error saving'));
    }
  };

  const deleteIncome = async (id) => {
    if (!window.confirm(t('finances.confirmDeleteIncome', 'Delete this income entry?'))) return;
    try {
      await api.delete(`/incomes/${id}`);
      toast.success(t('finances.incomeDeleted', 'Income deleted'));
      fetchIncome();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t('common.deleteError', 'Error deleting'));
        fetchIncome();
      } else {
        toast.error(t('common.deleteError', 'Error deleting'));
      }
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm(t('finances.confirmDeleteExpense', 'Delete this expense?'))) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success(t('finances.expenseDeleted', 'Expense deleted'));
      fetchExpenses();
    } catch (error) {
      toast.error(t('common.deleteError', 'Error deleting'));
    }
  };

  const editIncome = (income) => {
    setSelectedIncome(income);
    setIncomeForm({
      description: income.description,
      amount: income.amount,
      date: income.date,
      category: income.category,
      vat_rate: income.vat_rate,
      project_id: income.project_id || ""
    });
    setIncomeDialogOpen(true);
  };

  const editExpense = (expense) => {
    setSelectedExpense(expense);
    setExpenseForm({
      description: expense.description,
      amount: expense.amount,
      date: expense.date,
      category: expense.category,
      vat_rate: expense.vat_rate,
      is_deductible: expense.is_deductible
    });
    setExpenseDialogOpen(true);
  };

  const resetIncomeForm = () => {
    setIncomeForm({
      description: "",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      category: "service",
      vat_rate: 0,
      project_id: ""
    });
    setSelectedIncome(null);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      description: "",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      category: "office",
      vat_rate: 0,
      is_deductible: true
    });
    setSelectedExpense(null);
  };

  // Calculate totals (all amounts are stored as gross/brutto including VAT)
  const grossRevenue = income.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  const grossExpenses = expenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  
  // Calculate VAT (extracted from gross amounts)
  const vatCollected = income.reduce((sum, item) => {
    const amount = parseFloat(item.amount || 0);
    const vatRate = parseFloat(item.vat_rate || 0);
    return sum + (amount * vatRate / (100 + vatRate));
  }, 0);
  
  const vatPaid = expenses.reduce((sum, item) => {
    const amount = parseFloat(item.amount || 0);
    const vatRate = parseFloat(item.vat_rate || 0);
    return sum + (amount * vatRate / (100 + vatRate));
  }, 0);
  
  // Net amounts (without VAT)
  const netRevenue = grossRevenue - vatCollected;
  const netExpenses = grossExpenses - vatPaid;
  
  // Profits
  const grossProfit = grossRevenue - grossExpenses;
  const netProfit = netRevenue - netExpenses;

  const formatCurrency = (amount) => {
    const currency = countryInfo?.currency || '€';
    return `${parseFloat(amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t('finances.title', 'Finances')}</h1>
          <p className="text-gray-600">{t('finances.subtitle', 'Monitor your revenue, expenses, and profitability')}</p>
        </div>
      </div>

      {/* Financial Overview Cards - Top Row: Revenue & Expenses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Einnahmen (Gesamt)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Bruttoumsatz (inkl. USt)</span>
                <span className="text-lg font-bold text-emerald-600">{formatCurrency(grossRevenue)}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <span className="text-sm text-gray-600">Nettoumsatz (ohne USt)</span>
                <span className="text-lg font-bold text-emerald-700">{formatCurrency(netRevenue)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-red-600" />
              Ausgaben (Gesamt)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Bruttoausgaben (inkl. USt)</span>
                <span className="text-lg font-bold text-red-600">{formatCurrency(grossExpenses)}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <span className="text-sm text-gray-600">Nettoausgaben (ohne USt)</span>
                <span className="text-lg font-bold text-red-700">{formatCurrency(netExpenses)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Overview Cards - Bottom Row: Profits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Bruttogewinn</CardTitle>
            <p className="text-xs text-gray-500">inkl. USt</p>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${grossProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {formatCurrency(grossProfit)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Nettogewinn</CardTitle>
            <p className="text-xs text-gray-500">ohne USt</p>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${netProfit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
              {formatCurrency(netProfit)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* USt Overview Card */}
      <Card className="mb-6 border border-gray-200">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Umsatzsteuer-Übersicht</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="text-xs font-medium text-gray-500 mb-1">USt eingenommen</div>
              <div className="text-xl font-bold text-gray-900">{formatCurrency(vatCollected)}</div>
              <p className="text-xs text-gray-500 mt-2">Aus Brutto-Einnahmen</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="text-xs font-medium text-gray-500 mb-1">Vorsteuer gezahlt</div>
              <div className="text-xl font-bold text-gray-900">{formatCurrency(vatPaid)}</div>
              <p className="text-xs text-gray-500 mt-2">Aus Brutto-Ausgaben</p>
            </div>
            <div className="p-4 bg-gray-50 border-2 border-gray-900 rounded-lg">
              <div className="text-xs font-semibold text-gray-900 mb-1">USt-Zahllast</div>
              <div className="text-xl font-bold text-gray-900">{formatCurrency(vatCollected - vatPaid)}</div>
              <p className="text-xs text-gray-600 mt-2">= Eingenommen - Gezahlt</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t('finances.income', 'Income')}</CardTitle>
            <Dialog open={incomeDialogOpen} onOpenChange={(open) => {
              setIncomeDialogOpen(open);
              if (!open) resetIncomeForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('finances.newIncome', 'New Income')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {selectedIncome ? t('finances.editIncome', 'Edit Income') : t('finances.newIncome', 'New Income')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleIncomeSubmit} className="space-y-4">
                  <div>
                    <Label>{t('finances.description', 'Description')}</Label>
                    <Input
                      value={incomeForm.description}
                      onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>{t('finances.amount', 'Amount')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={incomeForm.amount}
                      onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>{t('finances.date', 'Date')}</Label>
                    <Input
                      type="date"
                      value={incomeForm.date}
                      onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>{t('finances.category', 'Category')}</Label>
                    <Select value={incomeForm.category} onValueChange={(value) => setIncomeForm({ ...incomeForm, category: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Service</SelectItem>
                        <SelectItem value="product">Product</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Project ({t('common.optional', 'optional')})</Label>
                    <Select value={incomeForm.project_id} onValueChange={(value) => setIncomeForm({ ...incomeForm, project_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('finances.selectProjectOptional', 'Select project (optional)')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">---</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('finances.vatRate', 'VAT Rate')}</Label>
                    <Select value={String(incomeForm.vat_rate)} onValueChange={(value) => setIncomeForm({ ...incomeForm, vat_rate: parseFloat(value) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% ({t('finances.taxFree', 'Tax-free')})</SelectItem>
                        {countryInfo?.vat_rates?.map((rate, index) => (
                          <SelectItem key={index} value={String(rate.rate)}>
                            {rate.rate}% - {rate.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => {
                      setIncomeDialogOpen(false);
                      resetIncomeForm();
                    }}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button type="submit">
                      {selectedIncome ? t('common.update', 'Update') : t('common.create', 'Create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {income.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('finances.noIncome', 'Noch keine Einnahmen vorhanden.')}</p>
              ) : (
                income.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{item.description}</p>
                      <p className="text-sm text-gray-600">{new Date(item.date).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-emerald-600">{formatCurrency(item.amount)}</span>
                      <Button size="sm" variant="ghost" onClick={() => editIncome(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteIncome(item.id)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t('finances.expenses', 'Expenses')}</CardTitle>
            <Dialog open={expenseDialogOpen} onOpenChange={(open) => {
              setExpenseDialogOpen(open);
              if (!open) resetExpenseForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('finances.newExpense', 'New Expense')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {selectedExpense ? t('finances.editExpense', 'Edit Expense') : t('finances.newExpense', 'New Expense')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleExpenseSubmit} className="space-y-4">
                  <div>
                    <Label>{t('finances.description', 'Description')}</Label>
                    <Input
                      placeholder={t('finances.descriptionExample', 'e.g. Office supplies')}
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>{t('finances.amount', 'Amount')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>{t('finances.date', 'Date')}</Label>
                    <Input
                      type="date"
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>{t('finances.category', 'Category')}</Label>
                    <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="office">Office</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="travel">Travel</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('finances.vatRate', 'VAT Rate')}</Label>
                    <Select value={String(expenseForm.vat_rate)} onValueChange={(value) => setExpenseForm({ ...expenseForm, vat_rate: parseFloat(value) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% ({t('finances.taxFree', 'Tax-free')})</SelectItem>
                        {countryInfo?.vat_rates?.map((rate, index) => (
                          <SelectItem key={index} value={String(rate.rate)}>
                            {rate.rate}% - {rate.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="deductible"
                      checked={expenseForm.is_deductible}
                      onChange={(e) => setExpenseForm({ ...expenseForm, is_deductible: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="deductible">{t('finances.isDeductible', 'Tax Deductible')}</Label>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => {
                      setExpenseDialogOpen(false);
                      resetExpenseForm();
                    }}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button type="submit">
                      {selectedExpense ? t('common.update', 'Update') : t('common.create', 'Create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {expenses.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('finances.noExpenses', 'Noch keine Ausgaben vorhanden.')}</p>
              ) : (
                expenses.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{item.description}</p>
                      <p className="text-sm text-gray-600">{new Date(item.date).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-red-600">{formatCurrency(item.amount)}</span>
                      <Button size="sm" variant="ghost" onClick={() => editExpense(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteExpense(item.id)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
